/**
 * User Site Roles Admin Page
 *
 * Manage user-site-role assignments: assign roles to users at specific sites,
 * view existing assignments, and revoke them.
 *
 * SOC 2 Controls:
 * - CC6.2: Role-based access control management
 * - CC6.3: User lifecycle management (assignment expiration, revocation)
 * - CC7.2: Audit trail (mutations logged server-side)
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import TabNavigation from '../../components/TabNavigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { UserCog, Plus, X, Trash2, Search, Info } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrg } from '../../context/OrgContext';
import { usePermissions } from '../../context/PermissionContext';

// BUG-019/020: Site roles feature is temporarily disabled. When false, the
// page renders a "feature disabled" banner instead of the assignment UI.
// Backend site-role mutation endpoints return 503; the flag keeps the page
// usable once the feature is re-enabled without code changes.
const SITE_ROLES_ENABLED = false;

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const UserSiteRoles = ({ user, onLogout, hideNavigation = false }) => {
  const { token } = useAuth();
  const { sites } = useOrg();
  const { isAdmin } = usePermissions();

  // Short-circuit if the feature is disabled — show a stub message
  if (!SITE_ROLES_ENABLED) {
    const stub = (
      <div className="p-6 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              Site Role Assignments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <Info className="w-4 h-4" />
              <AlertDescription>
                Site role assignment is temporarily disabled. Please manage user access via the standard Role Assignment page.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
    if (hideNavigation) return stub;
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        {stub}
      </div>
    );
  }

  // State
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [backendReady, setBackendReady] = useState(true);

  // Filters
  const [filterUserId, setFilterUserId] = useState('');
  const [filterSiteId, setFilterSiteId] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ user_id: '', site_node_id: '', role_id: '', expires_at: '' });
  const [addError, setAddError] = useState(null);
  const [adding, setAdding] = useState(false);

  // Confirm revoke — retained for when site-role revocation is re-enabled
  // (backend currently 503s every attempt: BUG-019/020).
  // eslint-disable-next-line no-unused-vars
  const [revokeId, setRevokeId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Fetch users list
  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/tenant-admin/users`, { headers });
      setUsers(res.data.users || res.data || []);
    } catch (err) {
      console.warn('[UserSiteRoles] Could not fetch users:', err.message);
    }
  }, [token]);

  // Fetch roles list
  const fetchRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/tenant-admin/roles`, { headers });
      const allRoles = res.data.roles || res.data || [];
      // Filter to site-scoped roles if scope_type field exists
      setRoles(allRoles);
    } catch (err) {
      console.warn('[UserSiteRoles] Could not fetch roles:', err.message);
    }
  }, [token]);

  // Fetch all site role assignments
  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_BASE}/api/rbac/user-site-roles`;
      if (filterUserId) {
        url = `${API_BASE}/api/rbac/user-site-roles/${filterUserId}`;
      }
      const res = await axios.get(url, { headers });
      setAssignments(res.data.assignments || res.data || []);
      setBackendReady(true);
    } catch (err) {
      if (err.response?.status === 404) {
        setBackendReady(false);
        setAssignments([]);
      } else {
        setError(err.response?.data?.detail || err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [token, filterUserId]);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchAssignments();
  }, [fetchUsers, fetchRoles, fetchAssignments]);

  // Add assignment
  const handleAdd = async () => {
    if (!addForm.user_id || !addForm.role_id) {
      setAddError('User and Role are required');
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      await axios.post(`${API_BASE}/api/rbac/user-site-roles`, {
        user_id: addForm.user_id,
        site_node_id: addForm.site_node_id || null,
        role_id: addForm.role_id,
        expires_at: addForm.expires_at || null
      }, { headers });
      setShowAddModal(false);
      setAddForm({ user_id: '', site_node_id: '', role_id: '', expires_at: '' });
      fetchAssignments();
    } catch (err) {
      setAddError(err.response?.data?.detail || err.message);
    } finally {
      setAdding(false);
    }
  };

  // Revoke assignment — retained for when site-role revocation is re-enabled
  // (backend currently 503s every attempt: BUG-019/020).
  // eslint-disable-next-line no-unused-vars
  const handleRevoke = async (id) => {
    try {
      await axios.delete(`${API_BASE}/api/rbac/user-site-roles/${id}`, { headers });
      setRevokeId(null);
      fetchAssignments();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  // Get status badge
  const getStatusBadge = (assignment) => {
    if (assignment.revoked) {
      return <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">Revoked</span>;
    }
    if (assignment.expires_at && new Date(assignment.expires_at) < new Date()) {
      return <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">Expired</span>;
    }
    return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">Active</span>;
  };

  // Filter assignments
  const filteredAssignments = assignments.filter(a => {
    if (filterSiteId && a.site_node_id !== filterSiteId) return false;
    if (filterRole && a.role_name !== filterRole && a.role_id !== filterRole) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const userName = (a.user_name || a.user_email || '').toLowerCase();
      const siteName = (a.site_name || '').toLowerCase();
      if (!userName.includes(term) && !siteName.includes(term)) return false;
    }
    return true;
  });

  if (!isAdmin()) {
    return (
      <div className="p-8 text-center text-gray-500">
        <p>You do not have permission to manage site role assignments.</p>
      </div>
    );
  }

  const content = (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UserCog className="w-6 h-6" />
            User Site Role Assignments
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Assign roles to users at specific sites. Corporate roles have site_node_id = null (all sites access).
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-2" disabled={!backendReady}>
          <Plus className="w-4 h-4" />
          Add Assignment
        </Button>
      </div>

      {!backendReady && (
        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <Info className="w-4 h-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            Site role assignment endpoints are not yet available (Phase 1 backend required).
            User-level roles can be managed in <a href="/admin/users/roles" className="underline font-medium">Role Management</a>.
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert className="mb-6" variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-4">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="User or site name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Filter by User</label>
              <select
                value={filterUserId}
                onChange={(e) => setFilterUserId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Users</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Filter by Site</label>
              <select
                value={filterSiteId}
                onChange={(e) => setFilterSiteId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Sites</option>
                {(sites || []).map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Filter by Role</label>
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Roles</option>
                {roles.map(r => (
                  <option key={r.id || r.name} value={r.name}>{r.name}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Assignments Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : filteredAssignments.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {backendReady ? 'No site role assignments found.' : 'Waiting for backend Phase 1 endpoints.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">User</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Site</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Role</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Assigned By</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Assigned At</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Expires</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredAssignments.map((a) => (
                    <tr key={a.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">{a.user_name || a.user_email || a.user_id}</td>
                      <td className="px-4 py-3">{a.site_name || (a.site_node_id ? a.site_node_id : <span className="text-gray-400 italic">Corporate (All Sites)</span>)}</td>
                      <td className="px-4 py-3 font-medium">{a.role_name || a.role_id}</td>
                      <td className="px-4 py-3 text-gray-500">{a.assigned_by_name || a.assigned_by || '—'}</td>
                      <td className="px-4 py-3 text-gray-500">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-3 text-gray-500">{a.expires_at ? new Date(a.expires_at).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-3">{getStatusBadge(a)}</td>
                      <td className="px-4 py-3 text-right">
                        {/* Site-role revocation is disabled server-side (BUG-019/020);
                            the backend answers 503 for every attempt. Keep the control
                            visible-but-disabled instead of offering an action that
                            always fails. Re-enable alongside the backend fix. */}
                        {!a.revoked && (
                          <button
                            disabled
                            className="text-gray-300 p-1 cursor-not-allowed"
                            title="Site role revocation is temporarily disabled (BUG-019/020)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Assignment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Site Role Assignment</h3>
              <button onClick={() => { setShowAddModal(false); setAddError(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {addError && (
              <Alert className="mb-4" variant="destructive">
                <AlertDescription>{addError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User <span className="text-red-500">*</span></label>
                <select
                  value={addForm.user_id}
                  onChange={(e) => setAddForm({ ...addForm, user_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select user...</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name || u.email}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site</label>
                <select
                  value={addForm.site_node_id}
                  onChange={(e) => setAddForm({ ...addForm, site_node_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Corporate (All Sites)</option>
                  {(sites || []).map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Leave empty for corporate-level roles</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role <span className="text-red-500">*</span></label>
                <select
                  value={addForm.role_id}
                  onChange={(e) => setAddForm({ ...addForm, role_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select role...</option>
                  {roles.map(r => (
                    <option key={r.id || r.name} value={r.id}>{r.name}{r.scope_type ? ` (${r.scope_type})` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expires At</label>
                <input
                  type="date"
                  value={addForm.expires_at}
                  onChange={(e) => setAddForm({ ...addForm, expires_at: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">Optional. Leave empty for permanent assignment.</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => { setShowAddModal(false); setAddError(null); }}>Cancel</Button>
              <Button onClick={handleAdd} disabled={adding}>
                {adding ? 'Assigning...' : 'Assign Role'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (hideNavigation) return content;

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      {content}
    </div>
  );
};

export default UserSiteRoles;
